import { flexRender, Row } from '@tanstack/react-table';
import React, { Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Spacer } from 'src/common';
import { MOTION_TRANSITION } from 'src/config';
import { GTInternalIds } from '../types';
import * as S from './GTRow.styles';

interface GTRowProps<T> {
  row: Row<T>;
  content?: React.ReactNode;
  itemHeight: number;
  disabled?: boolean;
  tableId?: string;
  hideExpandContentWhenClosed?: boolean;
}

export const GTRow = <T,>({
  row,
  content,
  itemHeight,
  disabled,
  tableId,
  hideExpandContentWhenClosed,
}: GTRowProps<T>) => {
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  return (
    <Fragment key={row.id}>
      <S.Tr $itemHeight={itemHeight} data-cy={`gtrow-${tableId}`} data-expanded={isExpanded} $disabled={disabled}>
        <td className="gt-left-spacer" />
        {row.getVisibleCells().map((cell) => {
          const { size, maxSize, minSize } = cell.column.columnDef;
          const isInternal = Object.values(GTInternalIds).includes(cell.column.id as GTInternalIds);
          return (
            <td
              key={cell.id}
              className={isInternal ? undefined : 'gt-data-cell'}
              style={{
                maxWidth: maxSize,
              }}
            >
              <S.TdContent $size={size} $maxSize={maxSize} $minSize={minSize} data-cy="gt-row-content">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </S.TdContent>
            </td>
          );
        })}
      </S.Tr>
      {content && (
        <S.ExpandTr>
          <td colSpan={row.getVisibleCells().length + 1}>
            <motion.div
              className="ext-content"
              initial={{ height: 0, marginTop: 0, marginBottom: 0 }}
              animate={{
                height: isExpanded ? 'unset' : 0,
                marginTop: isExpanded ? 20 : 0,
                marginBottom: isExpanded ? 20 : 0,
              }}
              transition={MOTION_TRANSITION}
            >
              <Spacer $px={5} />
              <AnimatePresence>
                {(hideExpandContentWhenClosed ? isExpanded : true) && (
                  <motion.div initial={false} exit={{ opacity: 0 }} transition={{ delay: 0.3, duration: 0 }}>
                    {content}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </td>
        </S.ExpandTr>
      )}
    </Fragment>
  );
};
